import { MapSceneError } from '../errors';
import type { ClientPoint, MapPoint } from '../types';

/**
 * Converts browser client coordinates to the current unrounded SVG map coordinate system.
 *
 * @param svgElement Current rendered SVG root.
 * @param point Browser pointer coordinates to transform.
 * @returns Corresponding point in the SVG viewBox coordinate system.
 * @throws {MapSceneError} When the browser does not expose an invertible SVG screen matrix.
 */
export function clientPointToMapPoint(params: {
  svgElement: SVGSVGElement;
  point: ClientPoint;
}): MapPoint {
  const screenMatrix = params.svgElement.getScreenCTM();
  if (screenMatrix === null) {
    throw new MapSceneError({
      code: 'COORDINATE_TRANSFORM_UNAVAILABLE',
      message: 'The SVG screen coordinate transform is unavailable.',
    });
  }

  try {
    const svgPoint = params.svgElement.createSVGPoint();
    svgPoint.x = params.point.clientX;
    svgPoint.y = params.point.clientY;
    const mapPoint = svgPoint.matrixTransform(screenMatrix.inverse());

    return { x: mapPoint.x, y: mapPoint.y };
  } catch (cause) {
    throw new MapSceneError({
      code: 'COORDINATE_TRANSFORM_UNAVAILABLE',
      message: 'The SVG screen coordinate transform is not invertible.',
      cause,
    });
  }
}
